import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertTriangle, BarChart3, CheckCircle, KeyRound, Loader2, RefreshCw, ShieldCheck, UploadCloud } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const windows = [7, 14, 30, 60, 90];

function MetricCard({ label, value, icon: Icon, tone, testId }) {
  return (
    <Card className="border border-zinc-200" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</p>
            <p className="mt-2 text-2xl font-bold tracking-tight text-zinc-950" data-testid={`${testId}-value`}>{value}</p>
          </div>
          <div className={`flex h-9 w-9 items-center justify-center rounded-md ${tone}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ children, testId }) {
  return <p className="py-10 text-center text-sm text-zinc-500" data-testid={testId}>{children}</p>;
}

const alertStyles = {
  critical: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-950',
  info: 'border-sky-200 bg-sky-50 text-sky-950',
};

const alertBadgeStyles = {
  critical: 'bg-red-600 text-white',
  warning: 'bg-amber-400 text-zinc-950',
  info: 'bg-sky-600 text-white',
};

function safeTestId(value) {
  return String(value || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function HealthAlertsPanel({ health }) {
  const alerts = health?.alerts || [];
  const summary = health?.summary || {};

  return (
    <Card className="border border-zinc-200 overflow-hidden" data-testid="lead-monitoring-health-alerts-card">
      <CardHeader className="border-b border-zinc-100 bg-zinc-50/70 pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base font-bold font-heading" data-testid="lead-monitoring-health-alerts-title">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Source/API-Key Health Alerts
            </CardTitle>
            <p className="mt-1 text-xs text-zinc-500" data-testid="lead-monitoring-health-alerts-subtitle">
              Checks recent failures and quiet landing-page sources every time this dashboard loads.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="lead-monitoring-health-alerts-summary">
            <Badge className="bg-red-600 text-white" data-testid="lead-monitoring-health-alerts-critical-count">{summary.critical || 0} critical</Badge>
            <Badge className="bg-amber-400 text-zinc-950" data-testid="lead-monitoring-health-alerts-warning-count">{summary.warning || 0} warning</Badge>
            <Badge className="bg-sky-600 text-white" data-testid="lead-monitoring-health-alerts-info-count">{summary.info || 0} info</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {alerts.length === 0 ? (
          <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-900" data-testid="lead-monitoring-health-alerts-empty">
            <ShieldCheck className="h-5 w-5" />
            <div>
              <p className="text-sm font-semibold">All monitored lead sources look healthy.</p>
              <p className="text-xs text-emerald-700">No failure spikes or quiet API keys were detected.</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="lead-monitoring-health-alerts-list">
            {alerts.map((alert) => {
              const rowId = safeTestId(alert.id || alert.label);
              return (
                <div
                  key={alert.id}
                  className={`rounded-md border p-4 ${alertStyles[alert.severity] || alertStyles.info}`}
                  data-testid={`lead-monitoring-health-alert-${rowId}`}
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={alertBadgeStyles[alert.severity] || alertBadgeStyles.info} data-testid={`lead-monitoring-health-alert-severity-${rowId}`}>
                          {alert.severity}
                        </Badge>
                        <p className="text-sm font-bold" data-testid={`lead-monitoring-health-alert-title-${rowId}`}>{alert.title}</p>
                      </div>
                      <p className="mt-2 text-sm" data-testid={`lead-monitoring-health-alert-message-${rowId}`}>{alert.message}</p>
                      <p className="mt-2 text-xs opacity-80" data-testid={`lead-monitoring-health-alert-action-${rowId}`}>{alert.suggested_action}</p>
                    </div>
                    <div className="grid shrink-0 gap-1 text-xs md:min-w-56" data-testid={`lead-monitoring-health-alert-meta-${rowId}`}>
                      <span><strong>Target:</strong> {alert.label || 'Unknown'}</span>
                      <span><strong>Source:</strong> {alert.source || '—'}</span>
                      <span><strong>DBA:</strong> {alert.dba_slug || '—'}</span>
                      <span><strong>Last event:</strong> {alert.last_event_at ? new Date(alert.last_event_at).toLocaleString() : '—'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SourceHealthWidget() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/external/leads/source-health', {
        params: { source: 'gmv-llc-landing', dba_slug: 'butler-county-junk-removal', days: 7 },
      });
      setHealth(data);
    } catch (err) {
      setHealth({ status: 'error', error: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusClass = health?.status === 'healthy' ? 'bg-emerald-50 text-emerald-700' : health?.status === 'warning' ? 'bg-amber-50 text-amber-700' : health?.status === 'quiet' ? 'bg-sky-50 text-sky-700' : 'bg-red-50 text-red-700';
  const lastLeadAt = health?.last_lead?.created_at ? new Date(health.last_lead.created_at).toLocaleString() : '—';
  const lastSms = health?.last_sms ? `${health.last_sms.message_type || 'sms'} · ${health.last_sms.delivery_status || health.last_sms.status || 'unknown'}` : '—';

  return (
    <Card className="border border-zinc-200" data-testid="source-health-widget">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-bold font-heading" data-testid="source-health-title">Butler Source Health</CardTitle>
            <p className="mt-1 text-xs text-zinc-500">bcjunkremoval.net → FieldCRM intake and SMS status</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={fetchHealth} disabled={loading} data-testid="source-health-refresh-button">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={statusClass} data-testid="source-health-status">{health?.status || 'loading'}</Badge>
          <Badge variant="secondary" className="bg-zinc-100 text-zinc-700" data-testid="source-health-source">{health?.source || 'gmv-llc-landing'}</Badge>
        </div>
        {health?.error ? <p className="text-sm text-red-600" data-testid="source-health-error">{health.error}</p> : (
          <div className="grid gap-2 text-sm text-zinc-700 sm:grid-cols-2 xl:grid-cols-4" data-testid="source-health-metrics">
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">7-day success</p><p className="font-semibold">{health?.totals?.success_rate || 0}%</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Events</p><p className="font-semibold">{health?.totals?.events || 0} total · {health?.totals?.failures || 0} failed</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Last lead</p><p className="font-semibold" data-testid="source-health-last-lead">{lastLeadAt}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Last SMS</p><p className="font-semibold" data-testid="source-health-last-sms">{lastSms}</p></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LeadMonitoringPage() {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/external/leads/monitoring', { params: { days } });
      setReport(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const sourceChart = useMemo(() => (report?.by_source || []).slice(0, 8).map(row => ({
    name: row.label || row.source || 'Unknown',
    total: row.total,
    failures: row.failure,
  })), [report]);

  const keyChart = useMemo(() => (report?.by_api_key || []).slice(0, 8).map(row => ({
    name: row.label || 'Unknown key',
    total: row.total,
    failures: row.failure,
  })), [report]);

  if (loading && !report) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="lead-monitoring-loading">
        <div className="h-8 w-64 rounded bg-zinc-200" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-md bg-zinc-200" />)}</div>
        <div className="grid gap-4 lg:grid-cols-2">{[1, 2].map(i => <div key={i} className="h-72 rounded-md bg-zinc-200" />)}</div>
      </div>
    );
  }

  const totals = report?.totals || {};
  const health = report?.health_alerts || { summary: {}, alerts: [] };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-monitoring-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="secondary" className="mb-3 bg-zinc-950 text-white" data-testid="lead-monitoring-badge">External Intake</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 font-heading" data-testid="lead-monitoring-title">Inbound Lead Monitoring</h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500" data-testid="lead-monitoring-description">Lead intake performance across API keys, landing-page sources, DBA slugs, validations, uploads, and failures.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none transition-colors focus:border-zinc-500"
            data-testid="lead-monitoring-window-select"
          >
            {windows.map(option => <option key={option} value={option}>Last {option} days</option>)}
          </select>
          <Button type="button" variant="outline" onClick={fetchReport} disabled={loading} data-testid="lead-monitoring-refresh-button">
            {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Refresh
          </Button>
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="lead-monitoring-error">{error}</div>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="lead-monitoring-kpis">
        <MetricCard label="Events" value={totals.events || 0} icon={Activity} tone="bg-zinc-100 text-zinc-900" testId="lead-monitoring-kpi-events" />
        <MetricCard label="Created Leads" value={totals.lead_successes || 0} icon={CheckCircle} tone="bg-emerald-50 text-emerald-700" testId="lead-monitoring-kpi-created" />
        <MetricCard label="Failures" value={totals.failures || 0} icon={AlertTriangle} tone="bg-red-50 text-red-700" testId="lead-monitoring-kpi-failures" />
        <MetricCard label="Test Validations" value={totals.validated_tests || 0} icon={BarChart3} tone="bg-sky-50 text-sky-700" testId="lead-monitoring-kpi-validated" />
        <MetricCard label="Photo Uploads" value={totals.photo_uploads || 0} icon={UploadCloud} tone="bg-amber-50 text-amber-700" testId="lead-monitoring-kpi-photos" />
        <MetricCard label="Active Alerts" value={health.summary?.total || 0} icon={AlertTriangle} tone="bg-orange-50 text-orange-700" testId="lead-monitoring-kpi-health-alerts" />
      </div>

      <SourceHealthWidget />

      <HealthAlertsPanel health={health} />

      <Card className="border border-zinc-200" data-testid="lead-monitoring-daily-card">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold font-heading">Daily Intake Volume</CardTitle></CardHeader>
        <CardContent data-testid="lead-monitoring-daily-chart">
          {report?.daily?.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={report.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#09090b" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="failure" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState testId="lead-monitoring-daily-empty">No intake events in this window.</EmptyState>}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border border-zinc-200" data-testid="lead-monitoring-source-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold font-heading">Volume by Source</CardTitle></CardHeader>
          <CardContent data-testid="lead-monitoring-source-chart">
            {sourceChart.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={sourceChart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#09090b" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="failures" fill="#dc2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState testId="lead-monitoring-source-empty">No source data yet.</EmptyState>}
          </CardContent>
        </Card>

        <Card className="border border-zinc-200" data-testid="lead-monitoring-key-card">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-bold font-heading"><KeyRound className="h-4 w-4" /> Volume by API Key</CardTitle></CardHeader>
          <CardContent data-testid="lead-monitoring-key-chart">
            {keyChart.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={keyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-10} height={54} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#0f766e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failures" fill="#dc2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState testId="lead-monitoring-key-empty">No API key data yet.</EmptyState>}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_1.25fr]">
        <Card className="border border-zinc-200" data-testid="lead-monitoring-dba-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold font-heading">DBA Breakdown</CardTitle></CardHeader>
          <CardContent>
            {(report?.by_dba || []).length === 0 ? <EmptyState testId="lead-monitoring-dba-empty">No DBA activity yet.</EmptyState> : (
              <div className="space-y-2" data-testid="lead-monitoring-dba-list">
                {report.by_dba.map((row, index) => {
                  const rowKey = `${row.dba_slug || row.label || 'unknown'}-${index}`;
                  return (
                  <div key={rowKey} className="rounded-md border border-zinc-200 p-3" data-testid={`lead-monitoring-dba-row-${safeTestId(rowKey)}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="min-w-0 truncate text-sm font-semibold text-zinc-900">{row.label}</p>
                      <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">{row.success_rate}% ok</Badge>
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">{row.total} events · {row.success} leads · {row.failure} failures · {row.photos} photos</p>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-zinc-200" data-testid="lead-monitoring-failures-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold font-heading">Recent Failures</CardTitle></CardHeader>
          <CardContent>
            {(report?.recent_failures || []).length === 0 ? <EmptyState testId="lead-monitoring-failures-empty">No failed submissions in this window.</EmptyState> : (
              <div className="overflow-x-auto" data-testid="lead-monitoring-failures-table">
                <table className="w-full min-w-[680px] text-left text-sm">
                  <thead className="border-b border-zinc-200 text-xs uppercase tracking-[0.12em] text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">DBA</th>
                      <th className="py-2 pr-3">Code</th>
                      <th className="py-2 pr-3">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recent_failures.map((row, index) => (
                      <tr key={`${row.created_at}-${index}`} className="border-b border-zinc-100" data-testid={`lead-monitoring-failure-row-${index}`}>
                        <td className="py-2 pr-3 text-xs text-zinc-500">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-3 text-xs text-zinc-700">{row.source || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-zinc-700">{row.dba_slug || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-zinc-700">{row.status_code || '—'}</td>
                        <td className="py-2 pr-3 text-xs text-red-700">{row.error || 'Unknown failure'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}